import React, { useState } from "react";
import hosys from "@/api/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, Plus, Loader2, AlertTriangle } from "lucide-react";
import moment from "moment";

const INCIDENT_TYPES = { accident: "Accident", near_miss: "Quasi accident", risk_situation: "Situació de risc", injury: "Lesió", dangerous_condition: "Condició perillosa" };
const SEVERITY_COLORS = { low: "bg-blue-100 text-blue-700", medium: "bg-amber-100 text-amber-700", high: "bg-orange-100 text-orange-700", critical: "bg-red-100 text-red-700" };

export default function WorkIncidentList({ venue_id, incidents, onChanged, isAdmin }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ incident_type: "accident", title: "", incident_date: new Date().toISOString().split("T")[0], location: "", description: "", affected_worker_name: "", severity: "medium", status: "open" });
  const [saving, setSaving] = useState(false);

  const openForm = (item = null) => {
    setEditing(item);
    setForm(item ? { ...form, ...item } : { incident_type: "accident", title: "", incident_date: new Date().toISOString().split("T")[0], location: "", description: "", affected_worker_name: "", severity: "medium", status: "open", venue_id });
    setShowForm(true);
  };

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleSave = async () => {
    if (!form.title || !form.incident_date) return;
    setSaving(true);
    if (editing?.id) {
      await hosys.entities.WorkIncident.update(editing.id, { ...form, venue_id });
    } else {
      await hosys.entities.WorkIncident.create({ ...form, venue_id });
    }
    setSaving(false);
    setShowForm(false);
    onChanged();
  };

  const handleDelete = async (id) => {
    await hosys.entities.WorkIncident.delete(id);
    onChanged();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">Incidents Laborals</h3>
        {isAdmin && (
          <Button onClick={() => openForm()} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2 text-sm" size="sm">
            <Plus className="w-4 h-4" /> Registrar incident
          </Button>
        )}
      </div>

      {incidents.length === 0 ? (
        <Card className="p-8 text-center border-0 shadow-sm"><p className="text-gray-500">Cap incident registrat</p></Card>
      ) : (
        <div className="space-y-2">
          {incidents.map(i => (
            <Card key={i.id} className={`p-4 border-0 shadow-sm border-l-4 ${i.severity === "critical" ? "border-l-red-500 bg-red-50" : "border-l-orange-300"}`}>
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {i.severity === "critical" && <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />}
                    <h4 className="font-semibold text-gray-900">{i.title}</h4>
                    <Badge className={`border-0 text-[10px] ${SEVERITY_COLORS[i.severity]}`}>{i.severity}</Badge>
                    <Badge className={`border-0 text-[10px] ${i.status === "closed" ? "bg-gray-100 text-gray-700" : i.status === "investigating" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{i.status}</Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{i.description}</p>
                  <div className="grid grid-cols-2 gap-3 text-xs text-gray-500">
                    <div>Data: {moment(i.incident_date).format("DD/MM/YYYY")}</div>
                    {i.location && <div>Lloc: {i.location}</div>}
                    {i.affected_worker_name && <div>Afectat: {i.affected_worker_name}</div>}
                    {i.responsible_name && <div>Investigador: {i.responsible_name}</div>}
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex gap-1 flex-shrink-0">
                    <Button variant="ghost" size="icon" className="w-7 h-7 text-gray-400 hover:text-[#7B2D3F]" onClick={() => openForm(i)}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="w-7 h-7 text-gray-400 hover:text-red-600" onClick={() => handleDelete(i.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={o => !o && setShowForm(false)}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar incident" : "Registrar incident"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Tipus d'incident *</Label><Select value={form.incident_type} onValueChange={v => set("incident_type", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(INCIDENT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Títol *</Label><Input value={form.title} onChange={e => set("title", e.target.value)} placeholder="Títol breu de l'incident" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data *</Label><Input type="date" value={form.incident_date} onChange={e => set("incident_date", e.target.value)} /></div>
              <div><Label>Hora</Label><Input type="time" value={form.incident_time || ""} onChange={e => set("incident_time", e.target.value)} /></div>
            </div>
            <div><Label>Lloc</Label><Input value={form.location || ""} onChange={e => set("location", e.target.value)} placeholder="On va passar" /></div>
            <div><Label>Descripció detallada</Label><Textarea value={form.description} onChange={e => set("description", e.target.value)} rows={3} placeholder="Narra què va passar..." /></div>
            <div><Label>Treballador afectat</Label><Input value={form.affected_worker_name || ""} onChange={e => set("affected_worker_name", e.target.value)} placeholder="Nom" /></div>
            <div><Label>Gravetat</Label><Select value={form.severity} onValueChange={v => set("severity", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="low">Baixa</SelectItem><SelectItem value="medium">Mitjana</SelectItem><SelectItem value="high">Alta</SelectItem><SelectItem value="critical">Crítica</SelectItem></SelectContent></Select></div>
            <div><Label>Acció immediata</Label><Input value={form.immediate_action || ""} onChange={e => set("immediate_action", e.target.value)} placeholder="Quina acció es va prendre" /></div>
            <div><Label>Responsable investigació</Label><Input value={form.responsible_name || ""} onChange={e => set("responsible_name", e.target.value)} placeholder="Nom" /></div>
            <div><Label>Estat</Label><Select value={form.status} onValueChange={v => set("status", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="open">Obert</SelectItem><SelectItem value="investigating">Investigant</SelectItem><SelectItem value="resolved">Resolt</SelectItem><SelectItem value="closed">Tancat</SelectItem></SelectContent></Select></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel·lar</Button>
            <Button onClick={handleSave} disabled={!form.title || !form.incident_date || saving} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{editing ? "Actualitzar" : "Registrar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}